import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Row, Button, LoadingSkeleton } from "@narmi/design_system";
import { entities } from "byzantine";
import { bulkPaymentSchema } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { LimitsForDisplayType } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import HeaderWithSteps from "../../HeaderWithSteps";
import Container from "../../Container";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import { PaymentDetailsSection } from ".";
import { useTemplateContext } from "./templates/TemplateContext";
import TemplateDetailsCard from "./templates/TemplateDetailsCard";

export const PaymentDetailsScreen = ({
  limits,
}: {
  limits: LimitsForDisplayType;
}) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { ach_templates: achTemplatesFeature } = useUserFeatures();
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const { submitForm, setValidationSchema } = form;
  const {
    isTemplateMode,
    template,
    loading: templateLoading,
    templateFields,
    convertedPayments,
    templateQueryString,
    hasPopulatedForm,
    markFormPopulated,
  } = useTemplateContext();

  // bootstrap to load recipients data
  const { loading, load } = entities.bulkPayments.bootstrap.useBootstrap();
  useEffect(() => {
    load();
  }, [load]);

  // when user lands on this screen, set validation schema to not include "payments"
  useEffect(() => {
    setValidationSchema(bulkPaymentSchema);
  }, []);

  // reset form when switching to manual mode (i.e, user was in template flow,
  // went back, and chose "create from scratch"). hasPopulatedForm is false
  // because TemplateContext resets it when templateId changes.
  useEffect(() => {
    if (!isTemplateMode && !hasPopulatedForm) {
      form.resetForm();
      // `description` is not in initialValues so resetForm won't clear it
      form.setFieldValue("description", "");
      markFormPopulated();
    }
  }, [isTemplateMode, hasPopulatedForm]); // eslint-disable-line

  // populate form with template data once template and recipients are loaded
  useEffect(() => {
    if (!isTemplateMode || !template || hasPopulatedForm) return;
    if (convertedPayments.length === 0 && template.paymentCount > 0) return;

    markFormPopulated();

    // reset all fields to clean defaults, then overlay template values.
    // explicit defaults prevent stale data from a previously selected template
    form.setValues({
      ...form.values,
      title: "",
      fromAccount: template.institutionAccount?.id ?? "",
      secCode: template.secCode ?? "",
      entryDesc: template.entryDesc ?? "",
      description: template.description ?? "",
      groupPosting: template.groupPosting ?? false,
      chosenExpediteOption: "",
      totalExpediteFees: undefined,
      payments: convertedPayments.length > 0 ? convertedPayments : [],
    });
  }, [isTemplateMode, template, convertedPayments]); // eslint-disable-line

  const handleClickNext = async () => {
    const { success } = await submitForm();

    if (!success) {
      return;
    }

    markFormPopulated();
    navigate(
      `/${PAYMENT_ACTIVITY_ROUTE}/bulk/new/payments${templateQueryString}`
    );
  };

  const isLoading = loading || templateLoading;

  return (
    <Container maxWidth="640px">
      <LoadingSkeleton isLoading={isLoading}>
        <HeaderWithSteps
          useDisplayFont
          headerText={l10n.getString("bulk-payment-header")}
        />
        {isTemplateMode && template && (
          <TemplateDetailsCard template={template} />
        )}
        <PaymentDetailsSection
          limits={limits}
          templateFields={
            isTemplateMode && Object.values(templateFields).some(Boolean)
              ? templateFields
              : undefined
          }
        />
      </LoadingSkeleton>
      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              label={l10n.getString(
                achTemplatesFeature ? "button-back" : "button-cancel"
              )}
              onClick={() => {
                navigate(
                  achTemplatesFeature
                    ? `/${PAYMENT_ACTIVITY_ROUTE}/bulk/new${templateQueryString}`
                    : `/${PAYMENT_ACTIVITY_ROUTE}/bulk`
                );
              }}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleClickNext}
            />
          </Row.Item>
        </Row>
      </div>
    </Container>
  );
};

export default PaymentDetailsScreen;
